import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, Switch, message } from "antd";
import ManageMySpending, { currentMonthOption } from "../ManageMySpending";
import { Button1, Checkbox, Radio, Select } from "../../index";
import { VerticalBar } from "../../vertical-bar/VerticalBar";
import { budgetsApi } from "../../../api/endpoints";
import { useAccounts } from "../../../data/live";

const CATEGORIES = [
  "Gas & Fuel",
  "Groceries",
  "Rent",
  "Utilities",
  "Entertainment",
  "Travel",
  "Health",
  "Education",
  "Other",
];

const PERIOD_OPTIONS = [
  { value: "MONTHLY", label: "Every month" },
  { value: "QUARTERLY", label: "Every 3 months" },
  { value: "YEARLY", label: "Yearly" },
];

const CHART_MONTHS = ["JAN", "FEB", "MAR", "APR"];

const CreateBudget = () => {
  const navigate = useNavigate();
  const { accounts } = useAccounts();

  const [selectedMonth, setSelectedMonth] = useState(currentMonthOption());
  const [category, setCategory] = useState("");
  const [period, setPeriod] = useState("MONTHLY");
  const [amount, setAmount] = useState("");
  const [currency, setCurrency] = useState("AUD");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const currencyOptions = (
    accounts.length
      ? [...new Set(accounts.map((a) => a.currency))]
      : ["AUD", "USD", "EUR", "GBP"]
  ).map((c) => ({ name: c, value: c }));

  const chartData = useMemo(
    () => [
      {
        label: "You",
        data: CHART_MONTHS.map((_, index) => 240 + index * 120),
        backgroundColor: "#1BBB6B",
        borderRadius: 4,
        borderSkipped: false,
      },
      {
        label: "Average",
        data: CHART_MONTHS.map((_, index) => 320 + index * 85),
        backgroundColor: "#F7A64F",
        borderRadius: 4,
        borderSkipped: false,
      },
    ],
    []
  );

  const handleSave = async () => {
    setError(null);
    if (!category) return setError("Choose a category");
    if (!amount || Number(amount) <= 0) return setError("Enter a valid amount");

    setSubmitting(true);
    try {
      await budgetsApi.set({
        category,
        limit: String(amount),
        currency,
        period,
      });
      message.success("Budget saved");
      navigate("/budget-tracking");
    } catch (e) {
      setError(e.response?.data?.error || e.message || "Failed to save budget");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ManageMySpending
      navName="Manage My Spending"
      selectedMonth={selectedMonth}
      onMonthChange={setSelectedMonth}
      pageClassName="dashboard-page create-budget-page"
      contentClassName="dashboard-content"
    >
      <div className="create-budget-card row mt-3 mx-0 rounded-3 border p-3 p-md-4 justify-content-between">
        <div className="create-budget-header d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div>
            <span className="dashboard-eyebrow">Budget workspace</span>
            <h2>Create New Budget</h2>
            <p>Set a spending limit, choose the period, and review recent category movement.</p>
          </div>
          <div className="d-flex gap-2 align-items-center">
            <span className="fs14px create-budget-muted">Advanced view</span>
            <Switch />
          </div>
        </div>

        <div className="col-lg-5 mt-4">
          <div className="create-budget-panel d-flex flex-column gap-3">
            <div className="d-flex flex-column gap-2">
              <span className="fs12px">Category</span>
              <Select
                value={category}
                options={CATEGORIES.map((c) => ({ name: c, value: c }))}
                defaultValue="Choose Category"
                className="create-budget-input"
                onChange={(e) => setCategory(e.target.value)}
              />
            </div>

            <div className="d-flex flex-column gap-2">
              <label className="fs12px">Period</label>
              <div className="create-budget-periods">
                {PERIOD_OPTIONS.map(({ value, label }) => (
                  <label
                    key={value}
                    className={`create-budget-period ${period === value ? "active" : ""}`}
                  >
                    <Radio
                      height="18px"
                      width="18px"
                      accentColor="green"
                      name="period"
                      value={value}
                      checked={period === value}
                      onChange={() => setPeriod(value)}
                    />
                    <span className="fs14px">{label}</span>
                  </label>
                ))}
              </div>
              <Checkbox className="fs12px">
                Start each new month with the previous month's leftover amount
              </Checkbox>
            </div>

            <div className="create-budget-amount-row">
              <div className="d-flex flex-column gap-2 w-100">
                <label className="fs12px">Amount</label>
                <Input
                  className="create-budget-input"
                  placeholder="Input amount"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <div className="d-flex flex-column gap-2 create-budget-currency">
                <label className="fs12px">Currency</label>
                <Select
                  value={currency}
                  options={currencyOptions}
                  defaultValue="AUD"
                  className="create-budget-input"
                  onChange={(e) => setCurrency(e.target.value)}
                />
              </div>
            </div>

            <span className="fs12px fw-medium create-budget-muted">
              We'll set a budget of <span>{amount ? `${amount} ${currency}` : "-"}</span>{" "}
              {period.toLowerCase()} for <span>{category || "(category)"}</span>.
            </span>

            {error && <span className="fs12px create-budget-error">{error}</span>}
          </div>
        </div>

        <div className="col-lg-6 mt-4">
          <div className="create-budget-panel create-budget-chart-panel d-flex flex-column gap-3">
            <span className="fs-5 fw-semibold">{category || "Category"} Spending</span>

            <div className="create-budget-chart border rounded-3 d-flex align-items-center justify-content-center">
              <VerticalBar label={CHART_MONTHS} legendSize={12} barChartData={chartData} />
            </div>

            <span className="fs12px fw-medium create-budget-muted">
              Historic spending for <span>{category || "this category"}</span>.
            </span>
          </div>
        </div>

        <div className="create-budget-actions d-flex gap-2 justify-content-md-end align-items-center justify-content-center mt-4">
          <Button1
            buttonName="Cancel"
            className="btn-outline-danger"
            onClick={() => navigate(-1)}
          />
          <Button1
            buttonName={submitting ? "Saving..." : "Save"}
            className="btn-dark"
            onClick={handleSave}
            disabled={submitting}
          />
        </div>
      </div>
    </ManageMySpending>
  );
};

export default CreateBudget;
